import React from 'react';
import { useNavigate } from 'react-router-dom';
import AnalyticsTab from './admin/AnalyticsTab';
import ContentGaps from './admin/ContentGaps';
import OnboardWizard from './ops/OnboardWizard';
import TenantSettings from './ops/TenantSettings';
import API_BASE_URL from '../config';
import { BRAND } from '../lib/brand';
import { useAuth } from '../context/AuthContext';
import {
  badge, btnPrimary, btnSmall, card, fontStack, inputStyle, warnBox,
} from './ops/ui';

// Operator console: onboard clients (ops/OnboardWizard), track every
// customer/demo, change a tenant's sites, assistant, domain and transit feed
// (ops/TenantSettings), convert demos to production, and see what visitors
// asked that a tenant's site doesn't answer (admin/ContentGaps). Admin-key authed
// (X-Admin-Key) — internal use only; this is the counterpart to the
// per-tenant /admin the customer sees.

function fmtDate(ts) {
  if (!ts) return '—';
  try { return new Date(ts * 1000).toLocaleString(); } catch { return '—'; }
}

function fmtElapsed(iso) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (Number.isNaN(s)) return '?';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const isVideoChannel = (url) => /youtube\.com|youtu\.be|vimeo\.com/i.test(url || '');

// One-click into a bot's own dashboard (/admin: Sources, Widget & Persona,
// analytics) — performs the same channel-login the customer would, using the
// key the ops overview already holds, so operators never paste credentials.
function OpenDashboardButton({ channelUrl, customerKey }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  if (!channelUrl || !customerKey) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <button
        style={{ ...btnSmall, marginRight: 0 }}
        disabled={busy}
        onClick={async () => {
          setBusy(true); setErr('');
          const ok = await login(channelUrl, customerKey);
          setBusy(false);
          if (ok) navigate('/admin'); else setErr('login failed');
        }}
      >
        {busy ? 'opening…' : 'Open dashboard →'}
      </button>
      {err && <span style={{ color: '#991b1b', fontSize: '0.78rem' }}>{err}</span>}
    </span>
  );
}

// Masked customer key with click-to-copy. Never renders the full secret —
// this page gets screen-shared during demos; the clipboard gets the real key.
function KeyChip({ value }) {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;
  const masked = value.length > 12 ? `${value.slice(0, 9)}…${value.slice(-4)}` : value;
  return (
    <span style={{ fontSize: '0.78rem', color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <code style={{ background: '#EDF1F5', padding: '1px 6px', borderRadius: '4px' }} title="customer key (masked)">{masked}</code>
      <button
        style={{ ...btnSmall, marginRight: 0, padding: '1px 7px' }}
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? 'copied ✓' : 'copy key'}
      </button>
    </span>
  );
}

const bareHost = (u) => (u || '').toLowerCase()
  .replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');

const hasOnboardingDraft = () => {
  try { return !!sessionStorage.getItem('opsOnboardDraft'); } catch { return false; }
};

// Documents per site host for one corpus, with configured sites that have
// nothing indexed yet called out (a sister site that failed to crawl).
function CorpusLine({ corpus }) {
  if (!corpus) return null;
  const hosts = Object.entries(corpus.by_host || {}).sort((a, b) => (b[1].webpage + b[1].pdf) - (a[1].webpage + a[1].pdf));
  return (
    <div style={{ fontSize: '0.78rem', color: BRAND.gray600, padding: '2px 0 6px 2px' }}>
      {corpus.domain && (corpus.domain_set === false ? (
        // the server's silent fallback (domain_profile.py), not a choice
        <span style={{ ...badge('#fef3c7', '#92400e'), marginLeft: 0, marginRight: '6px' }}
              title="No domain stored: answers with the pension guards and wording. Set it in Settings → Kind.">
          domain not set · answering as pension
        </span>
      ) : <span style={{ ...badge('#f3f4f6', '#4b5563'), marginLeft: 0, marginRight: '6px' }}>{corpus.domain}</span>)}
      {corpus.transit_enabled && <span style={{ ...badge('#d1fae5', '#065f46'), marginLeft: 0, marginRight: '6px' }}>trip planning on</span>}
      <b>{corpus.documents}</b> docs
      {hosts.map(([host, n]) => (
        <span key={host} style={{ marginLeft: '10px', whiteSpace: 'nowrap', display: 'inline-block' }}>
          {host}: {n.webpage} pages{n.pdf ? `, ${n.pdf} PDFs` : ''}
        </span>
      ))}
      {corpus.crawl_sources == null && <span style={{ marginLeft: '10px', color: BRAND.gray400 }}>· crawl config not stored (agency file or derived)</span>}
      {(corpus.empty_sources || []).length > 0 && (
        <div style={warnBox}>Nothing indexed yet from: {corpus.empty_sources.join(', ')}</div>
      )}
    </div>
  );
}

// Page-wide crawl banner (fixed to the top so it's visible wherever you
// clicked Recrawl). kind: started | conflict | finished | error; while the
// status poll reports a crawl, the banner shows it live.
const bannerPalette = {
  running: { background: '#dbeafe', color: '#1e3a8a', borderBottom: '1px solid #bfdbfe' },
  started: { background: '#d1fae5', color: '#065f46', borderBottom: '1px solid #a7f3d0' },
  finished: { background: '#d1fae5', color: '#065f46', borderBottom: '1px solid #a7f3d0' },
  conflict: { background: '#fef3c7', color: '#92400e', borderBottom: '1px solid #fde68a' },
  error: { background: '#fee2e2', color: '#991b1b', borderBottom: '1px solid #fecaca' },
};

function OpsConsole() {
  const [adminKey, setAdminKey] = React.useState(() => localStorage.getItem('opsAdminKey') || '');
  const [keyInput, setKeyInput] = React.useState('');
  const [customers, setCustomers] = React.useState(null);
  const [error, setError] = React.useState('');
  const [analyticsCorpus, setAnalyticsCorpus] = React.useState('');
  const [gapsCorpus, setGapsCorpus] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  // Onboarding flow + per-tenant settings panel
  const [onboarding, setOnboarding] = React.useState(hasOnboardingDraft);
  const [settingsFor, setSettingsFor] = React.useState(null); // site_id
  const [provisionResult, setProvisionResult] = React.useState(null);

  // Convert-to-production state
  const [converting, setConverting] = React.useState(null); // {name, site_url, slug}
  const [originsInput, setOriginsInput] = React.useState('');

  // Live crawl state
  const [crawlStatus, setCrawlStatus] = React.useState(null); // {running: {scope, startedAt, pid} | null}
  const [banner, setBanner] = React.useState(null); // {kind, site?, scope?, message?}
  const prevRunningRef = React.useRef(null);

  React.useEffect(() => { document.title = 'Ops Console | Inquiros'; }, []);

  const api = React.useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Key': adminKey,
        ...(opts.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401 || res.status === 403) {
      throw new Error('Admin key rejected — check it and try again.');
    }
    return { ok: res.ok, status: res.status, data };
  }, [adminKey]);

  const loadOverview = React.useCallback(async () => {
    setError('');
    try {
      const { ok, data } = await api('/api/admin/ops/overview');
      if (!ok) throw new Error(data.error || 'failed to load overview');
      setCustomers(data.customers || []);
    } catch (e) {
      setError(e.message);
      if (e.message.startsWith('Admin key rejected')) {
        localStorage.removeItem('opsAdminKey');
        setAdminKey('');
      }
    }
  }, [api]);

  React.useEffect(() => {
    if (adminKey) loadOverview();
  }, [adminKey, loadOverview]);

  const pollCrawlStatus = React.useCallback(async () => {
    try {
      const { ok, data } = await api('/api/admin/ops/crawl-status');
      if (ok) setCrawlStatus(data);
    } catch { /* best-effort — the console works without the status endpoint */ }
  }, [api]);

  const running = crawlStatus ? crawlStatus.running : null;
  const crawlRunning = !!running;

  React.useEffect(() => {
    if (!adminKey) return undefined;
    pollCrawlStatus();
    const id = setInterval(pollCrawlStatus, crawlRunning ? 4000 : 12000);
    return () => clearInterval(id);
  }, [adminKey, crawlRunning, pollCrawlStatus]);

  // While a crawl runs, refresh the overview so doc counts tick up live;
  // when it ends, refresh once more and announce it.
  React.useEffect(() => {
    if (!adminKey || !crawlRunning) return undefined;
    const id = setInterval(loadOverview, 15000);
    return () => clearInterval(id);
  }, [adminKey, crawlRunning, loadOverview]);

  React.useEffect(() => {
    const was = prevRunningRef.current;
    const now = crawlStatus ? crawlStatus.running : null;
    prevRunningRef.current = now;
    if (was && !now) {
      setBanner({ kind: 'finished', scope: was.scope });
      loadOverview();
    } else if (now) {
      // the live banner takes over once the crawler confirms it started
      setBanner((b) => (b && b.kind === 'started' ? null : b));
    }
  }, [crawlStatus, loadOverview]);

  const provision = async (payload) => {
    setBusy(true);
    setError('');
    setProvisionResult(null);
    try {
      const { ok, data } = await api('/api/admin/ops/provision', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!ok) throw new Error(data.error || 'provision failed');
      setProvisionResult(data);
      setConverting(null);
      setOriginsInput('');
      await loadOverview();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const recrawl = async (site) => {
    setBusy(true);
    setError('');
    try {
      const { ok, data } = await api('/api/admin/ops/recrawl', {
        method: 'POST',
        body: JSON.stringify({ site }),
      });
      if (!ok) throw new Error(data.error || 'recrawl failed');
      if (data.crawler_status === 409) {
        setBanner({ kind: 'conflict', site });
        if (data.crawler_response?.running) setCrawlStatus({ running: data.crawler_response.running });
      } else if (data.crawler_status >= 400) {
        throw new Error(data.crawler_response?.error || `crawler HTTP ${data.crawler_status}`);
      } else {
        setBanner({ kind: 'started', site });
        pollCrawlStatus();
      }
    } catch (e) {
      setBanner({ kind: 'error', site, message: e.message });
    } finally {
      setBusy(false);
    }
  };

  // ---- key gate ----
  if (!adminKey) {
    return (
      <div style={{ fontFamily: fontStack, background: BRAND.canvas, minHeight: '100vh', color: BRAND.ink900 }}>
        <div style={{ maxWidth: '400px', margin: '0 auto', padding: '110px 20px 60px' }}>
          <header style={{ textAlign: 'center', marginBottom: '28px' }}>
            <img src="/inquiros-logo.png" alt="Inquiros"
                 style={{ height: '40px', display: 'block', margin: '0 auto 8px' }} />
            <p style={{ fontSize: '0.95rem', color: BRAND.gray500, margin: 0 }}>Ops Console</p>
          </header>
          <form onSubmit={(e) => {
            e.preventDefault();
            localStorage.setItem('opsAdminKey', keyInput.trim());
            setAdminKey(keyInput.trim());
          }} style={{ ...card, padding: '28px' }}>
            <input
              type="password" placeholder="Admin API key" value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)} required style={inputStyle}
            />
            <button type="submit" style={{ ...btnPrimary, width: '100%' }}>Enter</button>
            {error && <p style={{ marginTop: '12px', fontSize: '0.85rem', color: BRAND.danger, textAlign: 'center' }}>{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  const bannerKind = banner ? banner.kind : (crawlRunning ? 'running' : null);
  const bannerText = (() => {
    if (!bannerKind) return null;
    if (bannerKind === 'error') return `Recrawl failed for ${banner.site}: ${banner.message}`;
    if (bannerKind === 'conflict') {
      return `Recrawl of ${banner.site} not started: a crawl is already running`
        + (running ? ` (${running.scope}, started ${fmtElapsed(running.startedAt)} ago)` : '');
    }
    if (bannerKind === 'started') return `Recrawl requested for ${banner.site}. Waiting for the crawler to pick it up…`;
    if (bannerKind === 'finished') return `Crawl finished: ${banner.scope}. Corpus counts refreshed.`;
    return `Crawl in progress: ${running.scope} · started ${fmtElapsed(running.startedAt)} ago · doc counts refresh live`;
  })();

  return (
    <div style={{ fontFamily: fontStack, background: BRAND.canvas, minHeight: '100vh', color: BRAND.ink900 }}>
      {bannerKind && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          padding: '12px 44px 12px 20px', fontSize: '0.875rem', fontFamily: fontStack, fontWeight: 500,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)', ...bannerPalette[bannerKind],
        }}>
          <style>{'@keyframes opsPulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.2 } }'}</style>
          {crawlRunning && (
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'currentColor',
                           animation: 'opsPulse 1.6s ease-in-out infinite', flexShrink: 0 }} />
          )}
          <span>{bannerText}</span>
          {banner && (
            <button
              onClick={() => setBanner(null)}
              aria-label="Dismiss"
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                       background: 'none', border: 'none', cursor: 'pointer', color: 'inherit',
                       fontSize: '1.1rem', lineHeight: 1, padding: '4px' }}
            >
              ×
            </button>
          )}
        </div>
      )}
      <header style={{ background: BRAND.surface, borderBottom: `1px solid ${BRAND.rule}` }}>
        <div style={{ maxWidth: '1050px', margin: '0 auto', padding: '13px 20px',
                      display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/inquiros-logo.png" alt="Inquiros" style={{ height: '24px', display: 'block' }} />
          <span aria-hidden="true" style={{ color: BRAND.rule }}>|</span>
          <h1 style={{ fontSize: '15px', fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>Ops Console</h1>
          <span style={{ flex: 1 }} />
          <div>
            <button style={btnSmall} onClick={loadOverview} disabled={busy}>Refresh</button>
            <button style={{ ...btnSmall, marginRight: 0 }}
                    onClick={() => { localStorage.removeItem('opsAdminKey'); setAdminKey(''); }}>
              Lock
            </button>
          </div>
        </div>
      </header>
      <main style={{ maxWidth: '1050px', margin: '0 auto', padding: '28px 20px 80px' }}>

      {error && (
        <p style={{ marginBottom: '16px', fontSize: '0.875rem', color: BRAND.danger }}>{error}</p>
      )}

      {/* Onboarding */}
      {onboarding ? (
        <OnboardWizard api={api} onLaunched={() => { loadOverview(); pollCrawlStatus(); }}
                       onClose={() => setOnboarding(false)} />
      ) : (
        <section style={{ ...card, marginBottom: '28px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Onboard a client</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: BRAND.gray500 }}>
              Look at their sites, choose how each is crawled, set up the assistant, and create the demo in one go.
            </p>
          </div>
          <button type="button" style={btnPrimary} onClick={() => setOnboarding(true)}>
            {hasOnboardingDraft() ? 'Resume onboarding' : 'Onboard a client'}
          </button>
        </section>
      )}

      {/* Provision result */}
      {provisionResult && (
        <section style={{ marginBottom: '28px', padding: '16px 20px', border: '1px solid #d1fae5', background: '#f0fdf4', borderRadius: '10px', fontSize: '0.85rem' }}>
          {provisionResult.mode === 'demo' && (
            <>
              <b>Demo provisioned.</b>{' '}
              <a href={provisionResult.demo_url} target="_blank" rel="noreferrer">{provisionResult.demo_url}</a>
              {' '}· crawl: {provisionResult.initial_crawl}
              {provisionResult.customer_reused ? ' · existing customer reused' : (
                <div style={{ marginTop: '6px' }}>
                  New customer key (save it): <code>{provisionResult.customer_key}</code>{' '}
                  <button style={btnSmall} onClick={() => navigator.clipboard.writeText(provisionResult.customer_key)}>
                    Copy key
                  </button>
                </div>
              )}
            </>
          )}
          {provisionResult.mode === 'production' && (
            <>
              <b>Production tenant created</b> ({provisionResult.site_id})
              {provisionResult.styling_copied_from_demo ? ' — demo styling copied.' : ' — no demo styling found; set it in Widget & Persona.'}
              <div style={{ marginTop: '8px' }}>Embed snippet for the client site:</div>
              <pre style={{ marginTop: '6px', padding: '10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflowX: 'auto', fontSize: '0.75rem' }}>
                {provisionResult.embed_snippet}
              </pre>
              <button style={btnSmall} onClick={() => navigator.clipboard.writeText(provisionResult.embed_snippet)}>
                Copy snippet
              </button>
            </>
          )}
        </section>
      )}

      {/* Convert dialog */}
      {converting && (
        <section style={{ ...card, marginBottom: '28px', padding: '20px' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '10px' }}>
            Convert to production — {converting.name}
          </h2>
          <p style={{ fontSize: '0.82rem', color: BRAND.gray500, marginBottom: '10px' }}>
            The client's real origins, one per line (the corpus and styling carry over — no retraining):
          </p>
          <textarea
            value={originsInput}
            onChange={(e) => setOriginsInput(e.target.value)}
            placeholder={'https://www.client.org\nhttps://client.org'}
            rows={3}
            style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
          />
          <button style={btnPrimary} disabled={busy} onClick={() => provision({
            mode: 'production',
            name: converting.name,
            site_url: converting.site_url,
            slug: converting.slug,
            origins: originsInput.split('\n').map((s) => s.trim()).filter(Boolean),
          })}>
            {busy ? 'Working…' : 'Create production tenant'}
          </button>
          <button style={{ ...btnSmall, marginLeft: '10px' }} onClick={() => setConverting(null)}>Cancel</button>
        </section>
      )}

      {analyticsCorpus && <section style={{ ...card, marginBottom: 18, padding: 20 }}>
        <h3>Analytics · {analyticsCorpus}</h3>
        <button type="button" style={btnSmall} onClick={() => setAnalyticsCorpus('')}>Close analytics</button>
        <AnalyticsTab corpusId={analyticsCorpus} adminKey={adminKey} />
      </section>}
      {gapsCorpus && <section style={{ ...card, marginBottom: 18, padding: 20 }}>
        <h3>Content gaps · {gapsCorpus}</h3>
        <button type="button" style={btnSmall} onClick={() => setGapsCorpus('')}>Close content gaps</button>
        <ContentGaps corpusId={gapsCorpus} adminKey={adminKey} operator />
      </section>}
      {/* Customers table */}
      {customers === null ? (
        <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading…</p>
      ) : customers.length === 0 ? (
        <p style={{ color: '#888', fontSize: '0.9rem' }}>No customers yet.</p>
      ) : customers.map((c) => (
        <section key={c.customer_key} style={{ ...card, marginBottom: '18px', padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <span style={{ fontWeight: 600 }}>{c.name || '(unnamed)'}</span>
              {c.status !== 'active' && <span style={badge('#fee2e2', '#991b1b')}>{c.status}</span>}
              {crawlRunning && c.channels.some((ch) => ch === running.scope || bareHost(ch) === bareHost(running.scope)) && (
                <span style={badge('#dbeafe', '#1e40af')}>crawling…</span>
              )}
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>
                {c.channels.map((ch) => <a key={ch} href={ch} target="_blank" rel="noreferrer" style={{ marginRight: '10px' }}>{ch}</a>)}
              </div>
              <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <KeyChip value={c.customer_key} />
                {c.channels.map((ch) => (
                  <React.Fragment key={ch}>
                    <OpenDashboardButton channelUrl={ch} customerKey={c.customer_key} />
                    <button type="button" style={btnSmall} onClick={() => setAnalyticsCorpus(ch)}>Analytics</button>
                    {!isVideoChannel(ch) && (
                      <button type="button" style={btnSmall} onClick={() => setGapsCorpus(ch)}>Content gaps</button>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#374151', textAlign: 'right' }}>
              {c.corpus
                ? <>corpus: <b>{c.corpus.documents}</b> docs{c.corpus.excluded ? ` (${c.corpus.excluded} excluded)` : ''}<br />
                    <span style={{ color: '#9ca3af' }}>last crawl {fmtDate(c.corpus.updated_at)}</span></>
                : <span style={{ color: '#9ca3af' }}>no website corpus</span>}
            </div>
          </div>

          <div style={{ marginTop: '12px' }}>
            {c.sites.length === 0 && (
              <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>no tenants</span>
            )}
            {c.sites.map((s, si) => (
              <React.Fragment key={s.site_id}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', padding: '7px 0', borderTop: '1px solid #f3f4f6', fontSize: '0.84rem' }}>
                <code style={{ background: '#EDF1F5', padding: '1px 6px', borderRadius: '4px' }}>{s.site_id}</code>
                {isVideoChannel(s.channel_url) ? (
                  <span style={badge('#f3f4f6', '#4b5563')}>video search</span>
                ) : (
                  <span style={s.is_demo ? badge('#e0e7ff', '#3730a3') : badge('#d1fae5', '#065f46')}>
                    {s.is_demo ? 'demo' : 'production'}
                  </span>
                )}
                {s.status !== 'active' && <span style={badge('#fee2e2', '#991b1b')}>{s.status}</span>}
                <span style={{ flex: 1 }} />
                {s.is_demo && s.demo_url && (
                  <a href={s.demo_url} target="_blank" rel="noreferrer" style={{ ...btnSmall, textDecoration: 'none', display: 'inline-block' }}>
                    Open demo
                  </a>
                )}
                {s.is_demo && !c.sites.some((o) => !o.is_demo && o.channel_url === s.channel_url) && (
                  <button style={btnSmall} disabled={busy} onClick={() => {
                    setConverting({
                      name: c.name || s.site_id.replace(/-demo$/, ''),
                      site_url: s.channel_url,
                      slug: s.site_id.replace(/-demo$/, ''),
                    });
                    setOriginsInput('');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}>
                    Convert to production…
                  </button>
                )}
                {!isVideoChannel(s.channel_url) && (
                  <button style={btnSmall} disabled={busy} onClick={() => recrawl(s.channel_url)}>
                    Recrawl
                  </button>
                )}
                {!isVideoChannel(s.channel_url) && (
                  <button type="button" style={btnSmall} aria-expanded={settingsFor === s.site_id}
                          onClick={() => setSettingsFor(settingsFor === s.site_id ? null : s.site_id)}>
                    Settings
                  </button>
                )}
              </div>
              {/* one corpus line per channel (a demo and its production tenant share the corpus) */}
              {c.sites.findIndex((o) => o.channel_url === s.channel_url) === si && <CorpusLine corpus={s.corpus} />}
              {settingsFor === s.site_id && (
                <TenantSettings api={api} siteId={s.site_id} onClose={() => setSettingsFor(null)}
                                onRecrawl={recrawl} onSaved={loadOverview} />
              )}
              </React.Fragment>
            ))}
          </div>
        </section>
      ))}
      </main>
    </div>
  );
}

export default OpsConsole;
